import { Flex, Heading, Skeleton, useToast } from '@chakra-ui/react';
import assert from 'assert';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/CreditAgreementFullArtifact';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/creditAgreementContext';
import { ExportJsonButton } from 'packages/creditAgreements/ui/ExportJsonButton';

type CreditAgreementProps = {
  creditAgreementId: string;
  creditAgreementsPath: string;
  organizationId: string;
};
export const CreditAgreement = ({
  creditAgreementId,
  creditAgreementsPath,
  organizationId,
}: CreditAgreementProps) => {
  const navigate = useNavigate();
  const toast = useToast();

  const {
    data: creditAgreementFullArtifactData,
  } = useGetCreditAgreementFullArtifactQuery({
    variables: {
      creditAgreementId,
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastAdminConfig.title = `Error loading credit agreement.`;
        toast(errorToastAdminConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  if (!creditAgreementFullArtifactData) {
    return <Skeleton />;
  }

  const {
    creditAgreementFullArtifact: { creditAgreement },
  } = creditAgreementFullArtifactData;
  let creditAgreementFullArtifact = null;
  try {
    creditAgreementFullArtifact = parseCreditAgreementFullArtifact(
      creditAgreement,
    );

    assert(
      creditAgreementFullArtifact,
      `Expect creditAgreementFullArtifact to exist for ${creditAgreementId}`,
    );
    return (
      <>
        <Flex mb="40px">
          <Heading size="lg" mr={5}>
            Credit Agreement
          </Heading>
          <ExportJsonButton
            creditFacilityId={creditAgreementFullArtifact.creditFacilityId}
            creditAgreementFullArtifact={creditAgreementFullArtifact}
          />
        </Flex>
        <CreditAgreementContextProvider
          initOrgSlugs={[]} // this is a non-wip view, orgSlugs are used to populate monaco editor in wip mode
          parties={creditAgreementFullArtifact.facilityMechanics.parties}
        >
          <CreditAgreementFullArtifact
            organizationId={organizationId}
            creditAgreementFullArtifact={creditAgreementFullArtifact}
          />
        </CreditAgreementContextProvider>
      </>
    );
  } catch (err) {
    errorToastAdminConfig.title = `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`;
    toast(errorToastAdminConfig);
    navigate(creditAgreementsPath);
  }

  throw new Error('unreachable code here');
};

export default CreditAgreement;
